import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpDown, Minus, Search } from 'lucide-react'
import { useStore, usePrograms, useProgramsLoading, useProgramQuery, useHasPermission } from '../../store/index.js'
import { useProgramList, useUpcomingPrograms } from '../../hooks/usePrograms.js'
import { programsApi, type CatalogFacets } from '../../lib/api.js'
import type { Program } from '../../types/index.js'
import { ANTENNA_ELEMENT_PROGRAM_TYPES } from '../../lib/utils.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import { CatalogFacetsPanel, type CatalogFacetSelection } from './CatalogFacetsPanel.js'
import { CatalogSummaryBar } from './CatalogSummaryBar.js'
import { ProgramTable, type CatalogSortKey } from './ProgramTable.js'
import { ProgramPreviewPanel } from './ProgramPreviewPanel.js'
import { catalogFacetsFallback } from './catalogFacetsFallback.js'
import { upcomingDisplayRows, visibleCatalogIds } from './upcomingBand.js'

import { ProgramBulkActions } from './ProgramBulkActions.js'
import { Spinner } from '../common/Spinner.js'
import { Button } from '../ui/button.js'
import './ProgramsView.css'

/**
 * Catalogue des programmes : facettes à gauche, tableau au centre, aperçu à
 * droite.
 *
 * Trois gestes se répondent. Les **facettes** disent ce que contient le
 * catalogue et combien de fiches chaque critère donnerait. Le **tableau** rend
 * genre, titre, série, durée et état comparables d'un coup d'œil, là où la
 * liste étroite précédente tronquait le titre et cachait le reste. L'**aperçu**
 * répond à « est-ce bien celle-là, et peut-elle partir ? » sans ouvrir le
 * formulaire — c'est lui qui détaille ce qui manque, pour éviter d'ouvrir une
 * fiche juste pour le découvrir.
 *
 * Sélectionner une ligne ouvre l'aperçu ; « Modifier la fiche » ouvre le
 * formulaire d'édition. Un clic ne fait donc plus quitter le catalogue.
 */

interface ProgramCatalogProps {
  channelId:         string | null
  /** Ouvre le formulaire d'édition. */
  onEditProgram:     (program: Program) => void
  onProgramsDeleted: (ids: string[]) => void
}

export const ProgramCatalog = memo(function ProgramCatalog({
  channelId, onEditProgram, onProgramsDeleted,
}: ProgramCatalogProps) {
  const { total, hasMore, loadingMore, loadMore, reload } = useProgramList(channelId)
  const allPrograms = usePrograms()
  const loading     = useProgramsLoading()
  const query       = useProgramQuery()
  const canDelete   = useHasPermission('PROGRAM_DELETE')
  const setProgramQuery    = useStore(state => state.setProgramQuery)
  const programsUpdateSeq  = useStore(state => state.programsUpdateSeq)

  // Les éléments d'antenne ont leur propre écran : ils n'entrent pas ici.
  const currentPrograms = useMemo(() => allPrograms.filter(p => !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(p.type)), [allPrograms])

  // « Demain et plus loin » : chargé à part, affiché seulement une fois la
  // bande dépliée. Replié, rien de ce groupe n'est visible, sélectionnable ni
  // ouvert dans l'aperçu.
  const upcoming = useUpcomingPrograms(channelId)
  const [upcomingExpanded, setUpcomingExpanded] = useState(false)
  const upcomingRows = useMemo(
    () => upcomingDisplayRows(upcoming.programs.filter(p => !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(p.type)), upcoming.reversed),
    [upcoming.programs, upcoming.reversed],
  )
  const programIds = useMemo(
    () => visibleCatalogIds(currentPrograms, upcomingRows, upcomingExpanded),
    [currentPrograms, upcomingRows, upcomingExpanded],
  )
  // Toutes les fiches visibles, pour l'aperçu, la sélection et les actions.
  const programs = useMemo(() => {
    if (!upcomingExpanded) return currentPrograms
    const byId = new Map([...currentPrograms, ...upcomingRows].map(program => [program.id, program]))
    return programIds.map(id => byId.get(id)!)
  }, [upcomingExpanded, currentPrograms, upcomingRows, programIds])

  const [previewId,   setPreviewId]   = useState<string | null>(null)
  const [checkedIds,  setCheckedIds]  = useState<Set<string>>(() => new Set())
  const [deleting,    setDeleting]    = useState(false)
  const [searchDraft, setSearchDraft] = useState(query.search ?? '')
  const [facets, setFacets] = useState<CatalogFacets | null>(null)
  const [previewClosed, setPreviewClosed] = useState(false)
  const fallbackFacets = useMemo(() => catalogFacetsFallback(currentPrograms, total), [currentPrograms, total])

  const scrollRef          = useRef<HTMLDivElement>(null)
  const sentinelRef        = useRef<HTMLDivElement>(null)
  const selectionAnchorRef = useRef<string | null>(null)
  const [sentinelVisible, setSentinelVisible] = useState(false)

  const preview = useMemo(
    () => programs.find(program => program.id === previewId) ?? null,
    [programs, previewId],
  )

  // Pen présente le catalogue comme un maître/détail : la première fiche
  // visible nourrit donc l'inspecteur dès l'arrivée des données.
  // La liste du jour, pas le groupe à venir : déplier la bande ne doit pas
  // changer la fiche d'accueil.
  useEffect(() => {
    if (!previewClosed && !previewId && currentPrograms[0]) setPreviewId(currentPrograms[0].id)
  }, [previewClosed, previewId, currentPrograms])

  // Une fiche sortie du filtre ne doit rester ni cochée ni en aperçu : elle
  // agirait sur un programme que l'opérateur ne voit plus.
  useEffect(() => {
    const visible = new Set(programIds)
    setCheckedIds(previous => {
      const next = new Set([...previous].filter(id => visible.has(id)))
      return next.size === previous.size ? previous : next
    })
    if (selectionAnchorRef.current && !visible.has(selectionAnchorRef.current)) {
      selectionAnchorRef.current = null
    }
    setPreviewId(current => current && visible.has(current) ? current : null)
  }, [programIds])

  // La recherche part après une pause de frappe : une requête par caractère
  // ferait défiler la liste sous les doigts.
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgramQuery({ search: searchDraft.trim() || undefined })
    }, 250)
    return () => clearTimeout(timer)
  }, [searchDraft, setProgramQuery])

  // ── Scroll infini ────────────────────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore) { setSentinelVisible(false); return }
    const observer = new IntersectionObserver(
      entries => setSentinelVisible(entries[0]?.isIntersecting ?? false),
      { root: scrollRef.current, rootMargin: '200px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore])

  useEffect(() => {
    if (sentinelVisible && hasMore && !loading && !loadingMore) void loadMore()
  }, [sentinelVisible, hasMore, loading, loadingMore, loadMore])

  // ── Sélection ────────────────────────────────────────────────────────────
  const handleCheck = useCallback((program: Program, checked: boolean, shiftKey: boolean) => {
    setCheckedIds(previous => shiftKey
      ? updateRangeSelection(programIds, previous, selectionAnchorRef.current, program.id, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(program.id)
          else next.delete(program.id)
          return next
        })())
    if (!shiftKey) selectionAnchorRef.current = program.id
  }, [programIds])

  const allChecked  = programIds.length > 0 && programIds.every(id => checkedIds.has(id))
  const someChecked = programIds.some(id => checkedIds.has(id))

  const handleCheckAll = useCallback(() => {
    setCheckedIds(allChecked ? new Set() : new Set(programIds))
    selectionAnchorRef.current = null
  }, [allChecked, programIds])

  const clearSelection = useCallback(() => {
    setCheckedIds(new Set())
    selectionAnchorRef.current = null
  }, [])

  // ── Tri ──────────────────────────────────────────────────────────────────
  const sortBy  = (query.sortBy as CatalogSortKey) ?? 'createdAt'
  const sortDir = (query.sortDir as 'asc' | 'desc') ?? 'desc'

  const handleSort = useCallback((key: CatalogSortKey) => {
    // Recliquer la colonne active inverse le sens plutôt que de retrier à
    // l'identique.
    setProgramQuery(key === sortBy
      ? { sortDir: sortDir === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortDir: 'asc' })
  }, [setProgramQuery, sortBy, sortDir])

  const handleSortPreset = useCallback((value: string) => {
    const [nextSortBy, nextSortDir] = value.split(':') as [CatalogSortKey, 'asc' | 'desc']
    setProgramQuery({ sortBy: nextSortBy, sortDir: nextSortDir })
  }, [setProgramQuery])

  // ── Facettes ─────────────────────────────────────────────────────────────
  const facetSelection: CatalogFacetSelection = {
    readiness:      query.readiness as CatalogFacetSelection['readiness'],
    mediaState:     query.mediaState as CatalogFacetSelection['mediaState'],
    programGenreId: query.programGenreId,
    // La requête transporte `isLive` en chaîne ('true'/'false') comme le reste
    // des booléens d'URL ; la facette raisonne, elle, en booléen.
    isLive:         query.isLive === 'true' ? true : undefined,
    archived:       query.archived,
  }

  const handleFacetChange = useCallback((selection: CatalogFacetSelection) => {
    setProgramQuery({
      readiness:      selection.readiness,
      mediaState:     selection.mediaState,
      programGenreId: selection.programGenreId,
      isLive:         selection.isLive ? 'true' : undefined,
      archived:       selection.archived,
      // Changer de périmètre change le nombre de pages : rester en page 4 des
      // actifs en ouvrant l'archive afficherait une liste vide.
      page:           1,
    })
  }, [setProgramQuery])

  /** Périmètre servant à recompter les facettes : sans les facettes elles-mêmes. */
  const facetBaseQuery = useMemo(() => ({
    catalog:           'programs',
    search:            query.search,
    seriesId:          query.seriesId,
    collectionId:      query.collectionId,
    programCategoryId: query.programCategoryId,
    archived:          query.archived,
  }), [query.search, query.seriesId, query.collectionId, query.programCategoryId, query.archived])

  // ── Suppression groupée ──────────────────────────────────────────────────
  const handleDeleteSelected = useCallback(async () => {
    const selected = programs.filter(program => checkedIds.has(program.id))
    if (!canDelete || selected.length === 0) return
    const confirmed = window.confirm(
      `Supprimer définitivement ${selected.length} programme${selected.length > 1 ? 's' : ''} ?\n\n`
      + 'Cette action est irréversible.',
    )
    if (!confirmed) return

    setDeleting(true)
    try {
      const removed: string[] = []
      for (const program of selected) {
        try {
          await programsApi.deleteFromCatalog(program.id, program.active !== false)
          removed.push(program.id)
        } catch {
          // Une fiche encore programmée est refusée par le serveur : on
          // poursuit avec les autres plutôt que d'abandonner tout le lot.
        }
      }
      if (removed.length > 0) onProgramsDeleted(removed)
      clearSelection()
      void reload()
    } finally {
      setDeleting(false)
    }
  }, [canDelete, checkedIds, programs, onProgramsDeleted, reload, clearSelection])

  if (!channelId) {
    return <div className="catalog-empty">Sélectionnez une chaîne pour afficher le catalogue.</div>
  }

  return (
    <div className="catalog-layout">
      <CatalogSummaryBar facets={facets} fallbackFacets={fallbackFacets} displayedCount={programs.length} />

      <div className="catalog-workspace">
        <CatalogFacetsPanel
          channelId={channelId}
          baseQuery={facetBaseQuery}
          selection={facetSelection}
          onChange={handleFacetChange}
          refreshKey={programsUpdateSeq}
          onLoaded={setFacets}
          fallbackFacets={fallbackFacets}
        />

        <div className="catalog-main">
        <div className="catalog-main__toolbar">
          <label className="catalog-main__search-wrap">
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              className="tp-input catalog-main__search"
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              placeholder="Rechercher par titre, série ou identifiant…"
              aria-label="Rechercher un programme"
            />
          </label>
          <label className="catalog-main__sort">
            <ArrowUpDown size={13} aria-hidden="true" />
            <select
              value={`${sortBy}:${sortDir}`}
              onChange={event => handleSortPreset(event.target.value)}
              aria-label="Trier les programmes"
            >
              <option value="createdAt:desc">Modifiés récemment</option>
              <option value="title:asc">Titre A → Z</option>
              <option value="title:desc">Titre Z → A</option>
              <option value="durationSec:desc">Durée décroissante</option>
              <option value="durationSec:asc">Durée croissante</option>
            </select>
          </label>
        </div>

        {(loading || checkedIds.size > 0) && (
          <div className="catalog-main__count">
            <span role="status">
              {loading
                ? <><Spinner /> Chargement…</>
                : `${checkedIds.size} sélectionné${checkedIds.size > 1 ? 's' : ''} · ${programs.length} sur ${total + (upcomingExpanded ? upcoming.total : 0)}`}
            </span>
            {checkedIds.size > 0 && (
            <>
              <ProgramBulkActions
                channelId={channelId}
                selectedIds={[...checkedIds]}
                canDelete={canDelete}
                allowSeriesChange
                selectionHasArchived={programs.some(p => checkedIds.has(p.id) && !p.active)}
                onDone={() => { clearSelection(); void reload() }}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon-xs"
                onClick={() => void handleDeleteSelected()}
                disabled={deleting || !canDelete}
                aria-label={`Supprimer ${checkedIds.size} programme${checkedIds.size > 1 ? 's' : ''}`}
                title={canDelete ? 'Supprimer les programmes sélectionnés' : 'Permission de suppression requise'}
              >
                <Minus />
              </Button>
            </>
            )}
          </div>
        )}

        <div className="catalog-main__scroll" ref={scrollRef}>
          <ProgramTable
            programs={programs}
            selectedId={previewId}
            checkedIds={checkedIds}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onSelect={program => { setPreviewClosed(false); setPreviewId(program.id) }}
            onOpen={onEditProgram}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            allChecked={allChecked}
            someChecked={someChecked}
            loading={loading}
            upcoming={{
              programs:    upcomingRows,
              total:       upcoming.total,
              expanded:    upcomingExpanded,
              loading:     upcoming.loading,
              hasMore:     upcoming.hasMore,
              loadingMore: upcoming.loadingMore,
              onToggle:    () => setUpcomingExpanded(open => !open),
              onLoadMore:  () => void upcoming.loadMore(),
            }}
          />

          {!loading && currentPrograms.length === 0 && (
            <div className="catalog-empty">Aucun programme ne correspond à ces critères.</div>
          )}

          {hasMore && <div className="catalog-infinite-sentinel" ref={sentinelRef} />}
          </div>

          <div className="catalog-main__footer">
            <span>
              {currentPrograms.length} programmes sur {total}
              {upcoming.total > 0 && ` · ${upcoming.total} à venir`}
            </span>
            {hasMore && (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore || loading}
              >
                {loadingMore ? 'Chargement…' : 'Charger la suite →'}
              </button>
            )}
          </div>
        </div>

        {preview && (
          <ProgramPreviewPanel
            program={preview}
            onEdit={onEditProgram}
            onClose={() => { setPreviewClosed(true); setPreviewId(null) }}
          />
        )}
      </div>
    </div>
  )
})
